import React from 'react';
import PropTypes from 'prop-types';
import { getPlural } from 'client/utils/plural';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';

import './reviews-summary.scss';

function getPercentage(partialValue, totalValue) {
  return ((100 * partialValue) / totalValue).toFixed(0);
}

const aiIcon = (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx={12} cy={12.3164} r={10.75} fill="white" stroke="url(#paint0_linear_1829_1459)" strokeWidth={1.5} />
    <path
      d="M10.06 15.6831L8.635 12.6352L5.5 11.2497L8.635 9.86432L10.06 6.81641L11.485 9.86432L14.62 11.2497L11.485 12.6352L10.06 15.6831ZM15.22 17.3164L14.515 15.7852L12.94 15.0997L14.515 14.3997L15.22 12.8831L15.94 14.3997L17.5 15.0997L15.94 15.7852L15.22 17.3164Z"
      fill="url(#paint1_linear_1829_1459)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1829_1459"
        x1={22.6375}
        y1={7.71641}
        x2={4.8125}
        y2={21.5164}
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7564F5" />
        <stop offset={0.5} stopColor="#2070E8" />
        <stop offset={1} stopColor="#008EA1" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_1829_1459"
        x1={17.05}
        y1={9.96641}
        x2={8.70711}
        y2={17.3481}
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7564F5" />
        <stop offset={0.5} stopColor="#2070E8" />
        <stop offset={1} stopColor="#008EA1" />
      </linearGradient>
    </defs>
  </svg>
);

export const ReviewsSummary = ({ averageRating, totalReviewsNumber, reviewNumbersPerRating, generatedSummary }) => (
  <div className="reviews-summary">
    <div className="d-flex align-items-center">
      <span className="size-36 fw-bold text-cool-gray-30 d-inline-block me-0_5">{averageRating}</span>
      <RatingStars rating={averageRating} className="size-24 text-cool-gray-30" />
    </div>
    <p className="size-14 text-cool-gray-40 mb-1_5">
      {totalReviewsNumber} {getPlural('review', totalReviewsNumber)}
    </p>
    <ul className="list-unstyled mb-1_5">
      {reviewNumbersPerRating.map(({ label, numberOfReviews }) => {
        const percentage = getPercentage(numberOfReviews, totalReviewsNumber);

        return (
          <li className="d-flex align-items-center mb-0_25" key={label}>
            <span className="size-14 text-cool-gray-30 fw-bold d-inline-block">{label} stars</span>
            <div className="pos-r flex-grow-1 mx-0_5">
              <div className="rating-bar rating-bar-base" />
              {percentage >= 1 && (
                <div className="rating-bar rating-bar-gradient pos-a" style={{ width: `${percentage}%` }} />
              )}
            </div>
            <span className="percentage-label text-start size-14 text-cool-gray-30 d-inline-block">{percentage}%</span>
          </li>
        );
      })}
    </ul>
    <div>
      <h3 className="text-cool-gray-10 size-16 fw-bold mb-0_25">Customer Summary</h3>
      <p className="text-cool-gray-30 size-16 mb-0_5">{generatedSummary}</p>
      <div className="d-flex align-items-center">
        <span className="d-inline-block me-0_5" aria-hidden>
          {aiIcon}
        </span>
        <span className="size-14 text-cool-gray-30">AI-generated summary of customer reviews</span>
      </div>
    </div>
  </div>
);

ReviewsSummary.propTypes = {
  totalReviewsNumber: PropTypes.number.isRequired,
  averageRating: PropTypes.number.isRequired,
  reviewNumbersPerRating: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      numberOfReviews: PropTypes.number.isRequired,
    })
  ).isRequired,
  generatedSummary: PropTypes.string.isRequired,
};
